import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ExternalLink, Loader2, MessageCircle, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '../../lib/api';
import { waUrl } from '../../lib/waShare';
import { formatErr } from '../../components/admin/billing/shared';
import { GoogleReviews, TechLeaderboard } from '../../components/admin/feedback/RatingBits';
import { ReviewSender } from '../../components/admin/feedback/ReviewSender';
import { ReviewFollowups } from '../../components/admin/feedback/ReviewFollowups';

const OVERDUE_MS = 2 * 24 * 3600 * 1000;
const isOverdue = (r) => r.rating <= 3 && (r.follow_up || 'open') === 'open' && Date.now() - new Date(r.rated_at).getTime() > OVERDUE_MS;

const when = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');
const Stars = ({ n, size = 14 }) => (
  <span className="inline-flex text-amber-400" aria-label={`${n} stars`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} fill={i <= n ? 'currentColor' : 'none'} className={i <= n ? '' : 'text-slate-300'} />)}
  </span>
);

const GoogleCard = ({ google, reviewUrl }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="google-rating-card">
    <div className="text-xs uppercase tracking-wider text-slate-500">Google Maps</div>
    {google?.rating ? (
      <>
        <div className="mt-1 flex items-baseline gap-2"><span className="text-3xl font-semibold text-slate-900" data-testid="google-rating-value">{google.rating.toFixed(1)}</span><Stars n={Math.round(google.rating)} /></div>
        <div className="text-xs text-slate-500" data-testid="google-rating-count">{google.count} reviews</div>
        {google.maps_url && <a href={google.maps_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-teal-700">Open on Maps <ExternalLink size={12} /></a>}
      </>
    ) : <p className="mt-1 text-sm text-slate-500">Add your Google Maps Place ID in Settings › Workshop profile to show your live rating.</p>}
    {reviewUrl && !google?.rating && <a href={reviewUrl} target="_blank" rel="noreferrer" className="mt-2 block text-xs text-teal-700">Review link</a>}
  </div>
);

const StatsRow = ({ stats, google, reviewUrl }) => {
  const max = Math.max(1, ...Object.values(stats.distribution));
  return (
    <div className="mb-6 grid gap-4 md:grid-cols-3">
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="feedback-average-card">
        <div className="text-xs uppercase tracking-wider text-slate-500">Your customers</div>
        <div className="mt-1 flex items-baseline gap-2"><span className="text-3xl font-semibold text-slate-900" data-testid="feedback-average">{stats.average ?? '—'}</span>{stats.average && <Stars n={Math.round(stats.average)} />}</div>
        <div className="text-xs text-slate-500">{stats.count} ratings · {stats.requests_sent} requests sent · {stats.google_clicks} went to Google</div>
        <div className="mt-3 space-y-1">
          {[5, 4, 3, 2, 1].map((s) => (
            <div key={s} className="flex items-center gap-2 text-xs text-slate-600" data-testid={`feedback-dist-${s}`}>
              <span className="w-3">{s}</span>
              <div className="h-2 flex-1 rounded-full bg-slate-100"><div className={`h-2 rounded-full ${s >= 4 ? 'bg-emerald-500' : s === 3 ? 'bg-amber-400' : 'bg-rose-500'}`} style={{ width: `${(stats.distribution[s] / max) * 100}%` }} /></div>
              <span className="w-6 text-right">{stats.distribution[s]}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="feedback-monthly-chart">
        <div className="text-xs uppercase tracking-wider text-slate-500">Average rating by month</div>
        <div className="mt-3 flex h-28 items-end gap-2">
          {stats.monthly.length === 0 && <p className="text-sm text-slate-400">No ratings yet.</p>}
          {stats.monthly.map((m) => (
            <div key={m.month} className="flex flex-1 flex-col items-center gap-1" title={`${m.average} from ${m.count}`}>
              <span className="text-[10px] font-semibold text-slate-600">{m.average}</span>
              <div className="w-full rounded-t bg-teal-500" style={{ height: `${(m.average / 5) * 80}px` }} />
              <span className="text-[10px] text-slate-400">{m.month.slice(5)}/{m.month.slice(2, 4)}</span>
            </div>
          ))}
        </div>
      </div>
      <GoogleCard google={google} reviewUrl={reviewUrl} />
    </div>
  );
};

const WinbackForm = ({ r, onDone }) => {
  const { authHeader } = useAuth();
  const [f, setF] = useState({ kind: 'pct', value: 10, cap: '', valid_days: 30 });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/feedback/${r.id}/winback`, {
        kind: f.kind, value: Number(f.value), cap: f.kind === 'pct' && f.cap ? Number(f.cap) : null, valid_days: Number(f.valid_days),
      }, { headers: authHeader() });
      setSent(data); onDone(data.offer.code);
      toast.success(`Code ${data.offer.code} created${data.emailed ? ' and emailed' : ''}`);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (sent) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={`winback-created-${r.id}`}>
        <span><b>{sent.offer.code}</b> · {sent.label}{sent.emailed ? ' · emailed' : ''}</span>
        {sent.phone && <a href={waUrl(sent.phone, sent.message)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 font-semibold" style={{ color: '#fff' }} data-testid={`winback-wa-${r.id}`}><MessageCircle size={12} /> Send on WhatsApp</a>}
      </div>
    );
  }
  const inp = 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs';
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs" data-testid={`winback-form-${r.id}`}>
      <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={inp} data-testid={`winback-kind-${r.id}`}>
        <option value="pct">% off</option><option value="flat">₹ off</option>
      </select>
      <input type="number" min="1" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} className={`${inp} w-20`} data-testid={`winback-value-${r.id}`} />
      {f.kind === 'pct' && <input type="number" min="1" placeholder="Cap ₹ (optional)" value={f.cap} onChange={(e) => setF({ ...f, cap: e.target.value })} className={`${inp} w-32`} data-testid={`winback-cap-${r.id}`} />}
      <span className="text-slate-500">valid</span>
      <input type="number" min="1" max="365" value={f.valid_days} onChange={(e) => setF({ ...f, valid_days: e.target.value })} className={`${inp} w-16`} data-testid={`winback-days-${r.id}`} />
      <span className="text-slate-500">days</span>
      <button type="button" disabled={busy || !Number(f.value)} onClick={create} className="rounded-full bg-amber-500 px-3 py-1 font-semibold disabled:opacity-50" style={{ color: '#fff' }} data-testid={`winback-create-${r.id}`}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : 'Create code'}
      </button>
    </div>
  );
};

const FeedbackRow = ({ r, onSave }) => {
  const [winback, setWinback] = useState(false);
  const [code, setCode] = useState(r.winback_code || '');
  const [notes, setNotes] = useState(r.notes || '');
  return (
    <div className={`border-b border-slate-100 p-4 last:border-0 ${r.rating <= 3 ? 'bg-rose-50/40' : ''}`} data-testid={`feedback-row-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><Stars n={r.rating} /><span className="text-sm font-semibold text-slate-900">{[r.prefix, r.customer_name].filter(Boolean).join(' ')}</span>
            {isOverdue(r) && <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold uppercase" style={{ color: '#fff' }} data-testid={`feedback-overdue-${r.id}`}>Overdue</span>}</div>
          <p className="text-xs text-slate-500">{[r.invoice_number, r.vehicle, r.phone || r.email, when(r.rated_at), r.google_clicked_at && 'went to Google'].filter(Boolean).join(' · ')}</p>
        </div>
        <select value={r.follow_up || 'open'} onChange={(e) => onSave(r.id, { follow_up: e.target.value })} data-testid={`feedback-followup-${r.id}`}
          className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs">
          <option value="open">Open</option><option value="contacted">Contacted</option><option value="resolved">Resolved</option>
        </select>
      </div>
      {r.comment && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700" data-testid={`feedback-comment-${r.id}`}>“{r.comment}”</p>}
      {r.rating <= 3 && (code && !winback
        ? <p className="mt-2 text-xs text-amber-700" data-testid={`winback-code-${r.id}`}>Win-back code sent: <b>{code}</b></p>
        : winback ? <WinbackForm r={r} onDone={setCode} />
          : <button type="button" onClick={() => setWinback(true)} className="mt-2 rounded-full border border-amber-300 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50" data-testid={`winback-open-${r.id}`}>Send win-back offer</button>)}
      {r.rating <= 3 && (
        <div className="mt-2 flex gap-2">
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Follow-up notes" className="flex-1 rounded-lg border border-slate-300 px-2 py-1 text-xs" data-testid={`feedback-notes-${r.id}`} />
          <button type="button" onClick={() => onSave(r.id, { notes })} className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold" data-testid={`feedback-notes-save-${r.id}`}>Save</button>
        </div>
      )}
    </div>
  );
};

export default function FeedbackPage() {
  const { authHeader } = useAuth();
  const [tab, setTab] = useState('due');
  const [filter, setFilter] = useState('');
  const [counts, setCounts] = useState(null);
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const f = await axios.get(`${API}/feedback`, { headers: authHeader(), params: filter ? { rating: filter } : {} });
      setData(f.data);
    } catch (e) { toast.error(formatErr(e)); }
  }, [authHeader, filter]);
  useEffect(() => { load(); }, [load]);

  const save = async (id, body) => {
    try {
      const { data: row } = await axios.patch(`${API}/feedback/${id}`, body, { headers: authHeader() });
      setData((d) => ({ ...d, rows: d.rows.map((r) => (r.id === id ? row : r)) }));
      toast.success('Saved');
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!data) return <div className="p-10 text-center text-slate-500"><Loader2 className="inline animate-spin" /></div>;
  const overdue = data.stats.overdue || 0;
  const tabCls = (on) => `rounded-full border px-3 py-1.5 text-xs font-semibold ${on ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-600'}`;
  return (
    <div className="min-h-screen px-4 py-6 sm:px-6" data-testid="feedback-page">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Customer feedback</h1>
      <p className="mb-6 text-sm text-slate-500">Star ratings from the day-after review request. Happy customers are pointed to Google; unhappy ones come to you first.</p>
      <StatsRow stats={data.stats} google={data.google} reviewUrl={data.review_url} />
      <div className="mb-3 flex flex-wrap gap-1.5">
        <button type="button" className={tabCls(tab === 'due')} onClick={() => setTab('due')} data-testid="feedback-tab-due">Send review requests{counts ? ` (${counts.pending} pending${counts.remind ? `, ${counts.remind} to remind` : ''})` : ''}</button>
        <button type="button" className={tabCls(tab === 'feedback')} onClick={() => setTab('feedback')} data-testid="feedback-tab-feedback">Feedback ({data.stats.count}){overdue > 0 && <span className="ml-1.5 rounded-full bg-rose-600 px-1.5 text-[10px]" style={{ color: '#fff' }} data-testid="feedback-overdue-count">{overdue} overdue</span>}</button>
        <button type="button" className={tabCls(tab === 'google')} onClick={() => setTab('google')} data-testid="feedback-tab-google">Google reviews</button>
        <button type="button" className={tabCls(tab === 'techs')} onClick={() => setTab('techs')} data-testid="feedback-tab-techs">Technicians</button>
        {tab === 'feedback' && (
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="ml-auto rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs" data-testid="feedback-filter">
            <option value="">All ratings</option><option value="low">1–3 stars</option><option value="high">4–5 stars</option>
          </select>
        )}
      </div>
      {tab === 'google' && <GoogleReviews />}
      {tab === 'techs' && <TechLeaderboard />}
      {tab === 'due' && <ReviewFollowups reviewUrl={data.review_url} />}
      {tab === 'due' && <ReviewSender onCounts={setCounts} />}
      {tab === 'feedback' && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="feedback-list">
          {data.rows.length === 0 && <p className="p-6 text-center text-sm text-slate-500">No feedback yet.</p>}
          {data.rows.map((r) => <FeedbackRow key={r.id} r={r} onSave={save} />)}
        </div>
      )}
    </div>
  );
}
